import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { safeConfirm } from '../../lib/confirm';
import { Plus, Trash2, Sparkles, Loader2, Search, Filter, Video as VideoIcon } from 'lucide-react';

const BLANK = {
  name: '', category: 'mobility', body_part: 'core', difficulty: 'beginner',
  purpose: '', description: '', instructions: '', equipment: '',
  image_url: '', video_url: '', sets: 3, reps: 10, rest_seconds: 60, duration_min: 10,
};

export default function AdminExercises() {
  const [items, setItems] = useState([]);
  const [bodyParts, setBodyParts] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [search, setSearch] = useState('');
  const [bpFilter, setBpFilter] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('Suggest 3 exercises for chronic lower back pain in a 65-year-old patient.');
  const [aiOut, setAiOut] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiProvider, setAiProvider] = useState('anthropic');

  const load = () => {
    const params = {};
    if (search) params.q = search;
    if (bpFilter) params.body_part = bpFilter;
    if (catFilter) params.category = catFilter;
    return api.get('/exercises', { params }).then(r => setItems(r.data));
  };
  const loadBodyParts = () => api.get('/exercises/body-parts').then(r => setBodyParts(r.data));
  useEffect(() => { load(); loadBodyParts(); /* eslint-disable-next-line */ }, [search, bpFilter, catFilter]);

  const startNew = () => { setEditing(null); setForm(BLANK); setShow(true); };
  const startEdit = (ex) => { setEditing(ex.exercise_id); setForm({ ...BLANK, ...ex }); setShow(true); };

  const submit = async () => {
    const body = {
      ...form,
      sets: parseInt(form.sets) || 0,
      reps: parseInt(form.reps) || 0,
      rest_seconds: parseInt(form.rest_seconds) || 0,
      duration_min: parseInt(form.duration_min) || 0,
    };
    if (editing) await api.put(`/exercises/${editing}`, body);
    else await api.post('/exercises', body);
    setShow(false); setEditing(null); load(); loadBodyParts();
  };
  const remove = async (id) => {
    if (!(await safeConfirm('Delete this exercise?'))) return;
    await api.delete(`/exercises/${id}`); load();
  };

  const runAI = async () => {
    setAiLoading(true); setAiOut('');
    try {
      const { data } = await api.post('/ai/generate', { prompt: aiPrompt, provider: aiProvider, purpose: 'exercise' });
      setAiOut(data.text);
    } catch (e) {
      setAiOut('AI error: ' + (e.response?.data?.detail || e.message));
    } finally { setAiLoading(false); }
  };

  return (
    <div className="p-8 max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">PROTOCOLS</div>
          <h1 className="text-4xl font-heading font-bold tracking-tight">Exercise library</h1>
          <p className="text-fg-muted mt-2">Curated exercises with name, purpose, instructions, equipment, sets/reps/rest and optional photo/video.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={()=>setAiOpen(!aiOpen)} className="btn-secondary inline-flex items-center gap-2" data-testid="ai-suggest-btn">
            <Sparkles className="w-4 h-4" /> AI suggestions
          </button>
          <button onClick={startNew} className="btn-primary inline-flex items-center gap-2" data-testid="new-exercise-btn">
            <Plus className="w-4 h-4" /> New
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-card mb-6 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[220px]">
          <label className="text-[11px] text-fg-muted uppercase tracking-wider">Search</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted"/>
            <input className="input pl-9" placeholder="Search name, purpose, equipment…"
                   value={search} onChange={e=>setSearch(e.target.value)} data-testid="ex-search"/>
          </div>
        </div>
        <div>
          <label className="text-[11px] text-fg-muted uppercase tracking-wider">Body part</label>
          <select className="input min-w-[180px]" value={bpFilter} onChange={e=>setBpFilter(e.target.value)} data-testid="ex-body-filter">
            <option value="">All</option>
            {bodyParts.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] text-fg-muted uppercase tracking-wider">Category</label>
          <select className="input min-w-[150px]" value={catFilter} onChange={e=>setCatFilter(e.target.value)} data-testid="ex-cat-filter">
            <option value="">All</option>
            {['mobility','strength','balance','rehab','stretching','stability','endurance'].map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        {(search || bpFilter || catFilter) && (
          <button onClick={()=>{ setSearch(''); setBpFilter(''); setCatFilter(''); }}
                  className="btn-secondary text-sm" data-testid="ex-clear-filters">
            <Filter className="w-3.5 h-3.5 inline mr-1"/> Clear
          </button>
        )}
        <div className="text-xs text-fg-muted ml-auto">{items.length} exercise{items.length === 1 ? '' : 's'}</div>
      </div>

      {aiOpen && (
        <div className="admin-card mb-6">
          <h3 className="font-heading font-semibold mb-3">AI exercise recommender</h3>
          <div className="flex gap-2 mb-2">
            <select className="input max-w-[220px]" value={aiProvider} onChange={e=>setAiProvider(e.target.value)} data-testid="ai-provider">
              <option value="anthropic">Claude Sonnet 4.5</option>
              <option value="openai">OpenAI GPT-5.2</option>
            </select>
          </div>
          <textarea className="input min-h-[80px]" value={aiPrompt} onChange={e=>setAiPrompt(e.target.value)} data-testid="ai-prompt" />
          <div className="mt-3 flex gap-2">
            <button onClick={runAI} disabled={aiLoading} className="btn-primary inline-flex items-center gap-2" data-testid="ai-run">
              {aiLoading && <Loader2 className="w-4 h-4 animate-spin" />} Generate
            </button>
          </div>
          {aiOut && (
            <div className="mt-4 surface border border-soft p-4 text-sm whitespace-pre-wrap" data-testid="ai-output">{aiOut}</div>
          )}
        </div>
      )}

      {show && (
        <div className="admin-card mb-6">
          <h3 className="font-heading font-semibold mb-4">{editing ? 'Edit exercise' : 'New exercise'}</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <Input label="Name *" v={form.name} on={v=>setForm({...form, name:v})} testid="ex-name" />
            <Input label="Body part *" v={form.body_part} on={v=>setForm({...form, body_part:v})} testid="ex-body" />
            <Select label="Category" v={form.category} on={v=>setForm({...form, category:v})}
                    options={['mobility','strength','balance','rehab','stretching','stability','endurance']} />
            <Select label="Difficulty" v={form.difficulty} on={v=>setForm({...form, difficulty:v})}
                    options={['beginner','intermediate','advanced']} />
            <Input label="Sets" type="number" v={form.sets} on={v=>setForm({...form, sets:v})} testid="ex-sets"/>
            <Input label="Reps" type="number" v={form.reps} on={v=>setForm({...form, reps:v})} testid="ex-reps"/>
            <Input label="Rest (seconds)" type="number" v={form.rest_seconds} on={v=>setForm({...form, rest_seconds:v})} testid="ex-rest"/>
            <Input label="Duration (min)" type="number" v={form.duration_min} on={v=>setForm({...form, duration_min:v})}/>
            <Input label="Equipment" v={form.equipment || ''} on={v=>setForm({...form, equipment:v})} placeholder="Bands, dumbbells, mat…" testid="ex-equipment"/>
            <div className="md:col-span-3">
              <label className="text-xs text-fg-muted">Purpose</label>
              <input className="input" value={form.purpose || ''} placeholder="Why this exercise — clinical goal in one sentence"
                     onChange={e=>setForm({...form, purpose: e.target.value})} data-testid="ex-purpose"/>
            </div>
            <Input label="Image URL" v={form.image_url || ''} on={v=>setForm({...form, image_url:v})}/>
            <Input label="Video URL (YouTube / Vimeo)" v={form.video_url || ''} on={v=>setForm({...form, video_url:v})} testid="ex-video-url"/>
            <div className="md:col-span-3">
              <label className="text-xs text-fg-muted">Description</label>
              <textarea className="input" value={form.description} onChange={e=>setForm({...form, description: e.target.value})} data-testid="ex-desc"/>
            </div>
            <div className="md:col-span-3">
              <label className="text-xs text-fg-muted">Instructions (step-by-step cues)</label>
              <textarea className="input min-h-[80px]" value={form.instructions || ''} onChange={e=>setForm({...form, instructions: e.target.value})}/>
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={()=>{ setShow(false); setEditing(null); }} className="btn-secondary">Cancel</button>
            <button onClick={submit} disabled={!form.name || !form.body_part} className="btn-primary" data-testid="ex-save">
              {editing ? 'Save changes' : 'Save'}
            </button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(ex => (
          <div key={ex.exercise_id} className="bento-card flex flex-col" data-testid={`exercise-card-${ex.exercise_id}`}>
            {ex.image_url && (
              <div className="rounded-lg overflow-hidden mb-3 -mx-1.5 -mt-1.5 h-36 relative">
                <img src={ex.image_url} alt={ex.name} className="w-full h-full object-cover" />
                {ex.video_url && (
                  <a href={ex.video_url} target="_blank" rel="noopener noreferrer"
                     className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5 hover:bg-black/80" title="Open video">
                    <VideoIcon className="w-3.5 h-3.5"/>
                  </a>
                )}
              </div>
            )}
            <div className="flex items-start justify-between">
              <h3 className="font-heading font-bold">{ex.name}</h3>
              <div className="flex gap-1">
                <button onClick={()=>startEdit(ex)} className="text-brand p-1" data-testid={`edit-${ex.exercise_id}`}>Edit</button>
                <button onClick={()=>remove(ex.exercise_id)} className="text-[#B83A3A] p-1" data-testid={`del-${ex.exercise_id}`}>
                  <Trash2 className="w-4 h-4"/>
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs mt-1 flex-wrap">
              <span className="badge-neutral">{ex.category}</span>
              <span className="badge-neutral">{ex.body_part}</span>
              <span className="badge-neutral">{ex.difficulty}</span>
              {ex.equipment && <span className="badge-neutral">{ex.equipment}</span>}
            </div>
            {ex.purpose && <p className="text-xs text-fg-muted italic mt-2">Purpose: {ex.purpose}</p>}
            <p className="text-sm text-fg-secondary mt-2 line-clamp-3">{ex.description}</p>
            <div className="text-xs text-fg-muted mt-auto pt-3 flex gap-3 border-t border-soft">
              <span>{ex.sets || 0} × {ex.reps || 0}</span>
              <span>{ex.rest_seconds || 0}s rest</span>
              {ex.duration_min ? <span>{ex.duration_min} min</span> : null}
            </div>
          </div>
        ))}
        {!items.length && (
          <div className="col-span-full text-center text-fg-muted py-12">
            No exercises match — clear filters or click <strong>New</strong> to add one.
          </div>
        )}
      </div>
    </div>
  );
}

function Input({ label, v, on, type='text', testid, placeholder }) {
  return (
    <div>
      <label className="text-xs text-fg-muted">{label}</label>
      <input className="input" value={v} type={type} placeholder={placeholder || ''}
             onChange={e=>on(e.target.value)} data-testid={testid} />
    </div>
  );
}
function Select({ label, v, on, options }) {
  return (
    <div>
      <label className="text-xs text-fg-muted">{label}</label>
      <select className="input" value={v} onChange={e=>on(e.target.value)}>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
